/**
 * USE_VARIATION_FORM - Form State Management Hook
 * 
 * SUMMARY:
 * Manages all variation form fields, dialog states, and basic form operations.
 * Handles input changes, file uploads, profit margin lock, and validation.
 * 
 * @returns {Object} Form state and handlers
 */

import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { taskFields } from '../../../../constants/fields.constant';
import { updateProfitMarginAction } from '../../../../slice/add-task';
import { errorSnackBar } from '../../../../actions/common.action';
import { fileValidator } from '../../../../util/validators/file';
import { updateTask } from '../../../../actions/project-management.action';

export const useVariationForm = ({ profitMargin, optionId, dialogClose, editMode }) => {
    const dispatch = useDispatch();

    // Form fields state
    const [fields, setFields] = useState(() => {
        const initialFields = JSON.parse(JSON.stringify(taskFields));
        if (profitMargin !== undefined && profitMargin !== null && profitMargin !== '') {
            initialFields.profitMargin.value = profitMargin;
        }
        return initialFields;
    });
    const [templateQty, setTemplateQty] = useState('');
    const [excludeFromFieldOps, setExcludeFromFieldOps] = useState(false);

    // Dialog states
    const [taskState, setTaskState] = useState({
        showlockIcon: true,
        profitMarginDialogState: false,
        showItemTemplateDialog: false,
        saveTemplateDialog: false,
        saveTemplateData: { type: '', data: {} }
    });

    const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);

    /**
     * Handle input changes for form fields
     * 
     * @param {Event} e - Input change event
     * @param {string} fieldName - Field name (optional)
     */
    const inputChangeHandler = (e, fieldName = "") => {
        if (e.target.type === "file") {
            const fileToUpload = e.target.files;
            Array.isArray(fileToUpload) && Array.from(fileToUpload).forEach(file => {
                const fileValidation = fileValidator({ file });
                const { validFileSize, validFileType, error } = fileValidation;

                if (!validFileSize.valid && !error) dispatch(errorSnackBar(validFileSize.error));
                else if (!validFileType.valid && !error) dispatch(errorSnackBar(validFileType.error));
                else if (error) dispatch(errorSnackBar(error.error));
                else fields[e.target.name].value.push(file);
            });
        } else {
            if (e.target.name === "profitMarginType") {
                fields["profitMargin"].value = "";
            }
            fields[e.target.name].value = e.target.value;
        }

        fields[e.target.name].touched = true;
        fields[e.target.name].error = false;
        fields[e.target.name].valid = true;

        setFields(prev => ({ ...prev, ...fields }));

        // Recalculate profit margin if needed
        if (e.target.name === "profitMarginType" || e.target.name === "profitMargin") {
            updateProfitChanges();
        }
    };

    /**
     * Update profit margin in Redux
     */
    const updateProfitChanges = () => {
        dispatch(updateProfitMarginAction({
            profitMargin: fields.profitMargin.value,
            profitMarginType: fields.profitMarginType.value,
        }));
    };

    /**
     * Toggle profit margin lock
     */
    const lockIconChange = async () => {
        const { showlockIcon } = taskState;
        const formData = new FormData();
        formData.set("overrideProfitMargin", showlockIcon);

        if (optionId) {
            await dispatch(updateTask(formData, optionId)).then((res) => { });
        }

        setTaskState(prev => ({
            ...prev,
            showlockIcon: !prev.showlockIcon
        }));

        if (!showlockIcon) {
            fields.profitMargin.value = profitMargin;
        }

        updateProfitChanges();
        setFields(prev => ({ ...prev, ...fields }));
        profitMarginDialogHandler();
    };

    /**
     * Toggle profit margin dialog
     */
    const profitMarginDialogHandler = () => {
        setTaskState(prev => ({
            ...prev,
            profitMarginDialogState: !prev.profitMarginDialogState
        }));
    };

    /**
     * Handle save template dialog
     * 
     * @param {string} type - Template type
     * @param {Object} data - Template data
     */
    const handleSaveTemplateDialog = (type = '', data = {}) => {
        setTaskState(prev => ({
            ...prev,
            saveTemplateDialog: !prev.saveTemplateDialog,
            saveTemplateData: { type, data }
        }));
    };

    /**
     * Get formatted file list for display
     * 
     * @returns {JSX.Element[]} Array of file elements
     */
    const getFiles = () => {
        const files = (fields.attachments.value || []).map((doc, i) => (
            <span key={i}> {doc.name}<span onClick={() => removeFile(i, "new")}>×</span></span>
        ));

        const attachments = (fields.attachments.attachmentArr || []).map((doc, i) => (
            <span key={i}> {doc.name}<span onClick={() => removeFile(i, "old")}>×</span></span>
        ));

        if (!files.length && !attachments.length) files.push(<span key={'space'}>&nbsp;</span>);
        return [...files, ...attachments];
    };

    /**
     * Remove file from attachments
     * 
     * @param {number} index - File index
     * @param {string} type - File type ('new' or 'old')
     */
    const removeFile = (index, type) => {
        if (type === "new") {
            const data = fields.attachments.value.filter((doc, i) => i !== index);
            fields.attachments.value = data;
        }
        if (type === "old") {
            const data = fields.attachments.attachmentArr.filter((doc, i) => i !== index);
            fields.attachments.attachmentArr = data;
        }
        setFields(prev => ({ ...prev, ...fields }));
    };

    /**
     * Handle close with confirmation
     */
    const handleCloseClick = () => {
        if (editMode) {
            setShowCloseConfirmDialog(true);
        } else {
            dialogClose && dialogClose();
        }
    };

    /**
     * Confirm close without saving
     */
    const handleCloseConfirm = () => {
        setShowCloseConfirmDialog(false);
        dialogClose && dialogClose();
    };

    /**
     * Cancel close dialog
     */
    const handleCloseCancel = () => {
        setShowCloseConfirmDialog(false);
    };

    return {
        // State
        fields,
        setFields,
        templateQty,
        setTemplateQty,
        excludeFromFieldOps,
        setExcludeFromFieldOps,
        taskState,
        setTaskState,
        showCloseConfirmDialog,
        setShowCloseConfirmDialog,

        // Handlers
        inputChangeHandler,
        updateProfitChanges,
        lockIconChange,
        profitMarginDialogHandler,
        handleSaveTemplateDialog,
        getFiles,
        removeFile,
        handleCloseClick,
        handleCloseConfirm,
        handleCloseCancel,
    };
};
